/**
 * useCalendarFeeds Hooks
 *
 * React Query hooks for managing a calendar's PUBLISHED webcal feeds (the
 * outbound, secret-URL counterpart to inbound subscriptions). Server-resource
 * CRUD, so per CLAUDE.md it goes through TanStack Query with keys from
 * `@/lib/query-keys` — never raw string arrays.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { api } from '@/lib/api';
import type { CalendarFeedDetail, CalendarFeedInput, CalendarFeedUpdate } from '../types';

/** List the published feeds for one calendar. Idle until a calendar is selected. */
export function useCalendarFeeds(calendarId: string | null) {
  return useQuery<CalendarFeedDetail[]>({
    queryKey: calendarId
      ? queryKeys.calendarFeeds.forCalendar(calendarId)
      : queryKeys.calendarFeeds.all,
    queryFn: async () => {
      const res = await api.listCalendarFeeds(calendarId as string);
      return res.feeds;
    },
    enabled: !!calendarId,
  });
}

/** Mint a feed. The raw URL is in the response ONCE — the caller must surface it
 *  immediately and never persist it. */
export function useCreateCalendarFeed(calendarId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CalendarFeedInput) => {
      if (!calendarId) throw new Error('No calendar selected');
      return api.createCalendarFeed(calendarId, input);
    },
    onSuccess: () => {
      if (calendarId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.calendarFeeds.forCalendar(calendarId),
        });
      }
    },
  });
}

/** Rotate a feed's token; the old URL dies immediately. Returns the new URL once. */
export function useRotateCalendarFeed(calendarId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (feedId: string) => api.rotateCalendarFeed(feedId),
    onSuccess: () => {
      if (calendarId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.calendarFeeds.forCalendar(calendarId),
        });
      }
    },
  });
}

/** Update a feed's label / visibility / window. */
export function useUpdateCalendarFeed(calendarId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ feedId, update }: { feedId: string; update: CalendarFeedUpdate }) =>
      api.updateCalendarFeed(feedId, update),
    onSuccess: () => {
      if (calendarId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.calendarFeeds.forCalendar(calendarId),
        });
      }
    },
  });
}

/** Revoke a feed (the URL 404s immediately). */
export function useRevokeCalendarFeed(calendarId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (feedId: string) => api.deleteCalendarFeed(feedId),
    onSuccess: () => {
      if (calendarId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.calendarFeeds.forCalendar(calendarId),
        });
      }
    },
  });
}
